import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-regular-svg-icons';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import { ReportTableColumns } from '../../../../../types/RectificationReportsTypes';
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks';
import { reportIsVisibleSelector, soldProductsByCustomerFiltersSelector } from '../../../../../../store/selectors/reportsSelectors';
import { SearchTableFilterType } from '../../../../../../core/types/coreTypes';
import { setSoldProductsByCustomerFilters } from '../../../../../../store/slices/reportsSlice';
import Thead from '../../../../../../core/components/table-components/Thead';
import Tr from '../../../../../../core/components/table-components/Tr';
import ThWithControls from '../../../../../../core/components/table-components/ThWithControls';
import ThNew from '../../../../../../core/components/table-components/table-new/ThNew';
import Dropdown from '../../../../../../core/components/dropdown/Dropdown';
import { useSoldProductsByCustomerRanges } from '../../../../../hooks/useSoldProductsByCustomerRanges';
import {
  getSoldProductsByCustomerReportExcelThunk,
  getSoldProductsByCustomerReportThunk,
} from '../../../../../../store/thunks/reports/reportsThunks';

const SoldProductsByCustomerColumns: React.FC<ReportTableColumns> = ({ columns, totalCount }) => {
  const dispatch = useAppDispatch();
  const reportIsVisible = useAppSelector(reportIsVisibleSelector);
  const filters = useAppSelector(soldProductsByCustomerFiltersSelector);

  const { range, setRange, rangePatterns } = useSoldProductsByCustomerRanges();
  const [searchVl, setSearchVl] = useState<SearchTableFilterType>({
    productNumber: filters.productNumber,
    productName: filters.productName,
  });

  useEffect(() => {
    if (!reportIsVisible) {
      setSearchVl({
        productNumber: undefined,
        productName: undefined,
      });
    }
  }, [reportIsVisible]);

  const sortHandle = (field: number) => {
    if (filters.order?.field === field) {
      const newFilters = {
        ...filters,
        order: { ...filters.order, isAsc: !filters.order.isAsc },
      };
      dispatch(setSoldProductsByCustomerFilters(newFilters));
      dispatch(getSoldProductsByCustomerReportThunk({ filters: newFilters }));
    } else {
      const newFilters = {
        ...filters,
        order: { field, isAsc: true },
      };
      dispatch(setSoldProductsByCustomerFilters(newFilters));
      dispatch(getSoldProductsByCustomerReportThunk({ filters: newFilters }));
    }
  };

  const onChangeSearchHandle = (v: string, field: string) => {
    setSearchVl({ ...searchVl, [field]: v });
    if (!v) {
      const newFilters = { ...filters, [field]: undefined, page: 1 };
      dispatch(setSoldProductsByCustomerFilters(newFilters));
      dispatch(getSoldProductsByCustomerReportThunk({ filters: newFilters }));
    }
  };
  const searchPressHandle = (field: string) => {
    const newFilters = { ...filters, [field]: searchVl[field]?.trim(), page: 1 };
    dispatch(setSoldProductsByCustomerFilters(newFilters));
    dispatch(getSoldProductsByCustomerReportThunk({ filters: newFilters }));
  };

  const setRangeHandle = (field: string, value: [number, number]) => {
    const [min, max] = value;
    const newFilters = { ...filters, [field]: { min, max }, page: 1 };
    dispatch(setSoldProductsByCustomerFilters(newFilters));
    dispatch(getSoldProductsByCustomerReportThunk({ filters: newFilters }));
  };

  const exportToExcel = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, pageSize, ...rest } = filters;
    dispatch(getSoldProductsByCustomerReportExcelThunk({ filters: rest }));
  };

  return (
    <Thead>
      <Tr>
        {columns.map((col) => (
          <ThWithControls
            key={col.dataIndex}
            col={col}
            tableFilters={filters}
            sortHandle={sortHandle}
            onChangeSearchHandle={onChangeSearchHandle}
            searchPressHandle={searchPressHandle}
            searchVl={searchVl}
            totalElements={totalCount}
            className={`soldProductsByCustomerTh--${col.dataIndex}`}
            disabledFilter={!reportIsVisible}
            range={range}
            rangePatterns={rangePatterns}
            setRange={setRange}
            setRangeHandle={setRangeHandle}
          />
        ))}
        <ThNew action>
          <Dropdown
            disabled={!totalCount}
            menuItems={[
              {
                label: 'Export to Excel',
                icon: <FontAwesomeIcon icon={faFileExcel} />,
                key: 'export-to-excel',
                onClick: exportToExcel,
              },
            ]}
            icon={<FontAwesomeIcon icon={faGear} />}
          />
        </ThNew>
      </Tr>
    </Thead>
  );
};
export default SoldProductsByCustomerColumns;
